import { useCallback, useEffect, useRef, useState } from 'react';
import type { ArtworkPublic } from '../../shared/types';

interface LightboxProps {
  items: ArtworkPublic[];
  startIndex: number;
  onClose: () => void;
}

export function Lightbox({ items, startIndex, onClose }: LightboxProps) {
  const [index, setIndex] = useState(startIndex);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const current = items[index];

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setIndex((i) => Math.min(items.length - 1, i + 1)), [items.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose]);

  // Preload immediate neighbors
  useEffect(() => {
    const preload = (url?: string) => {
      if (!url) return;
      const img = new Image();
      img.src = url;
    };
    preload(items[index - 1]?.full_url);
    preload(items[index + 1]?.full_url);
  }, [index, items]);

  if (!current) return null;

  function onTouchStart(e: React.TouchEvent) {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev();
      else goNext();
    }
    touchStart.current = null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={current.title}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 text-white"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between p-4">
        <div className="text-sm opacity-75">
          {index + 1} / {items.length}
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium hover:bg-white/20"
        >
          Close (Esc)
        </button>
      </div>

      <div
        className="flex flex-1 items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Previous"
          disabled={index === 0}
          onClick={goPrev}
          className="hidden rounded-full bg-white/10 p-3 text-2xl hover:bg-white/20 disabled:opacity-30 sm:block"
        >
          ‹
        </button>
        <img
          src={current.full_url}
          alt={current.title}
          width={current.full_width ?? undefined}
          height={current.full_height ?? undefined}
          className="mx-auto max-h-[80vh] w-auto max-w-full select-none object-contain animate-fade-in"
          draggable={false}
        />
        <button
          type="button"
          aria-label="Next"
          disabled={index === items.length - 1}
          onClick={goNext}
          className="hidden rounded-full bg-white/10 p-3 text-2xl hover:bg-white/20 disabled:opacity-30 sm:block"
        >
          ›
        </button>
      </div>

      <div className="p-6 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-xl">{current.title}</div>
        {current.description && (
          <div className="mx-auto mt-2 max-w-prose text-sm opacity-80">{current.description}</div>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs opacity-80">
          <span className="rounded-full bg-white/10 px-3 py-1">{current.category.name}</span>
          {current.tags.map((t) => (
            <span key={t.id} className="rounded-full bg-white/5 px-3 py-1">
              #{t.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
